import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Undo2, ShieldAlert, List } from "lucide-react";
import { fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, isSuperAdmin } from "@/lib/roles";
import RevertBulkAssignDialog from "@/pages/auditoria/RevertBulkAssignDialog";
import BloqueiosPanel from "@/pages/auditoria/BloqueiosPanel";

const ACTION_COLORS = {
  create: "bg-emerald-100 text-emerald-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-rose-100 text-rose-800",
  status_change: "bg-amber-100 text-amber-800",
  bulk_assign: "bg-indigo-100 text-indigo-800",
  bulk_delete: "bg-rose-200 text-rose-900",
  delete_all: "bg-rose-300 text-rose-900",
  import_csv: "bg-violet-100 text-violet-800",
  authorize_delegated: "bg-amber-200 text-amber-900",
  impersonate_start: "bg-sky-200 text-sky-900",
  impersonate_end: "bg-sky-100 text-sky-800",
  acesso_negado: "bg-rose-600 text-white",
};

const ACTION_LABELS = {
  create: "Criou",
  update: "Editou",
  delete: "Excluiu",
  status_change: "Alterou Status",
  bulk_assign: "Atribuição em massa",
  bulk_delete: "Exclusão em massa",
  delete_all: "Excluiu Tudo",
  import_csv: "Importou CSV",
  authorize_delegated: "Autorização delegada",
  impersonate_start: "Modo Suporte (entrada)",
  impersonate_end: "Modo Suporte (saída)",
  acesso_negado: "Acesso negado",
  acerto_registrado: "Registrou acerto",
  acerto_editado: "Editou acerto",
  acerto_desfeito: "Desfez acerto",
  export: "Exportou",
};

const ENTITY_LABELS = {
  entrega: "Entrega",
  acerto: "Acerto com entregador",
  entregadores: "Entregador",
  bairros: "Bairro",
  regioes: "Região",
  tipos_entrega: "Tipo de Entrega",
  formas_pgto: "Forma de Pagamento",
  usuario: "Usuário",
  empresa: "Empresa",
};

const trAction = (a) => ACTION_LABELS[a] || a;
const trEntity = (e) => ENTITY_LABELS[e] || e;

const STATUS_LABELS = { cancelada: "Cancelada", em_transito: "Em Trânsito", concluida: "Concluída", remarcar: "Remarcar" };

const ARTICLE_FEM = new Set(["Entrega", "Empresa", "Região", "Forma de Pagamento"]);

// Map { action → (log, ctx) => JSX } — substitui o switch/case gigante do frase().
const ACTION_RENDERERS = {
  create: (log, ctx) => {
    const { who, entity, article, nome, d } = ctx;
    if (entity === "Usuário" && d?.email) return <><b>{who}</b> criou o Usuário <b>{d.nome || "—"}</b> ({d.email}) com papel <i>{d.role}</i></>;
    if (entity === "Entrega") return <><b>{who}</b> criou uma <b>Entrega</b> {d?.numero_pedido ? `(pedido ${d.numero_pedido})` : ""} no valor de {d?.valor != null ? `R$ ${Number(d.valor).toFixed(2)}` : "—"}</>;
    return <><b>{who}</b> criou {article} <b>{entity}</b> {nome}</>;
  },
  update: (log, ctx) => {
    const { who, entity, article, idShort, d } = ctx;
    const campos = Array.isArray(d) ? d.filter((k) => !["updated_at", "id"].includes(k)).join(", ") : "";
    return <><b>{who}</b> editou {article} <b>{entity}</b> {idShort && `#${idShort}`} {campos && <span className="text-muted-foreground">(campos: {campos})</span>}</>;
  },
  delete: (log, ctx) => {
    const { who, entity, article, nome, idShort } = ctx;
    return <><b>{who}</b> excluiu {article} <b>{entity}</b> {nome || `#${idShort}`}</>;
  },
  status_change: (log, ctx) => {
    const { who, idShort, d } = ctx;
    return <><b>{who}</b> alterou o status da <b>Entrega</b> #{idShort} de <i>{STATUS_LABELS[d?.from] || d?.from}</i> para <i className="text-orange-700">{STATUS_LABELS[d?.to] || d?.to}</i>{d?.motivo ? ` — motivo: "${d.motivo}"` : ""}</>;
  },
  bulk_assign: (log, ctx) => <><b>{ctx.who}</b> atribuiu entregador para <b>{ctx.d?.modified || ctx.d?.ids?.length || 0}</b> entrega(s) em massa</>,
  bulk_delete: (log, ctx) => <><b>{ctx.who}</b> excluiu <b>{ctx.d?.deleted || ctx.d?.ids?.length || 0}</b> entrega(s) em massa</>,
  delete_all: (log, ctx) => <><b>{ctx.who}</b> executou <b className="text-rose-700">Limpar Tudo</b> — {ctx.d?.deleted || 0} entregas removidas</>,
  import_csv: (log, ctx) => <><b>{ctx.who}</b> importou CSV de <b>{ctx.entity}</b>: {ctx.d?.created || 0} criados, {ctx.d?.skipped || 0} ignorados, {ctx.d?.new_regioes || 0} regiões novas</>,
  authorize_delegated: (log, ctx) => (
    <>
      <b>{ctx.d?.authorizer_nome || "—"}</b>{" "}
      <span className="text-muted-foreground">({ctx.d?.authorizer_email || "—"})</span>{" "}
      autorizou <b>{ctx.who}</b> a executar{" "}
      <i>{ctx.d?.action === "e" ? "excluir" : ctx.d?.action}</i> em <b>{ctx.entity}</b>
    </>
  ),
  impersonate_start: (log, ctx) => (
    <><b>{ctx.who}</b> entrou em <b className="text-sky-700">Modo Suporte</b> na empresa <b>{ctx.d?.nome || "—"}</b></>
  ),
  impersonate_end: (log, ctx) => (
    <><b>{ctx.who}</b> saiu do <b className="text-sky-700">Modo Suporte</b></>
  ),
  acesso_negado: (log, ctx) => (
    <><b>{ctx.who}</b> <span className="text-rose-700 font-semibold">tentou acessar dados de outra empresa</span>
      {ctx.d?.alvo_empresa_nome ? <> (<b>{ctx.d.alvo_empresa_nome}</b>)</> : ""} — {ctx.d?.motivo || "bloqueado"}
      {ctx.d?.path ? <span className="text-muted-foreground mono text-xs"> · {ctx.d.method} {ctx.d.path}</span> : ""}
      {ctx.d?.ip ? <span className="text-muted-foreground"> · IP {ctx.d.ip}</span> : ""}</>
  ),
};

function frase(log) {
  const who = log.user_nome || "Usuário";
  const entity = trEntity(log.entity);
  const article = ARTICLE_FEM.has(entity) ? "a" : "o";
  const d = log.details;
  const idShort = (log.entity_id || "").slice(0, 8);
  const nome = d && typeof d === "object" && d.nome ? `"${d.nome}"` : (idShort ? `#${idShort}` : "");
  const ctx = { who, entity, article, d, idShort, nome };
  const renderer = ACTION_RENDERERS[log.action];
  if (renderer) return renderer(log, ctx);
  return <><b>{who}</b> {log.action} {entity} {idShort}</>;
}

export default function Auditoria() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ user: "all", action: "all", entity: "all", q: "" });
  const [revertLog, setRevertLog] = useState(null); // audit_log selecionado pra reverter
  const [view, setView] = useState("todos"); // todos | bloqueios
  const [resumo, setResumo] = useState(null);

  // Regra de permissão: SA sempre pode; demais precisam de `auditoria.e` (destructive
  // sobre logs — logs são append-only, então `e` fica disponível pra "executar ação").
  const canRevert = isSuperAdmin(user) || hasPerm(user, "auditoria", "e");

  const reload = () => {
    api.get("/audit-logs").then((r) => setLogs(r.data));
    api.get("/audit-logs/bloqueios/resumo").then((r) => setResumo(r.data)).catch(() => {});
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, []);

  const users = [...new Set(logs.map((l) => l.user_email).filter(Boolean))];
  const actions = [...new Set(logs.map((l) => l.action).filter(Boolean))];
  const entities = [...new Set(logs.map((l) => l.entity).filter(Boolean))];

  const filtered = logs.filter((l) => {
    if (filters.user !== "all" && l.user_email !== filters.user) return false;
    if (filters.action !== "all" && l.action !== filters.action) return false;
    if (filters.entity !== "all" && l.entity !== filters.entity) return false;
    if (filters.q && !JSON.stringify(l).toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  });

  const bloqueios24h = resumo?.ultimas_24h || 0;
  const tabCls = (active) => `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`;

  return (
    <div className="space-y-6" data-testid="page-auditoria">
      <div><h1 className="text-4xl font-extrabold flex items-center gap-3"><History className="w-9 h-9 text-orange-600"/>Auditoria</h1>
        <p className="text-muted-foreground">Log de ações dos usuários — quem mexeu, o quê e quando</p></div>

      <div className="flex flex-wrap items-center gap-2" data-testid="audit-tabs">
        <button type="button" className={tabCls(view === "todos")} onClick={() => setView("todos")} data-testid="audit-tab-todos" aria-pressed={view === "todos"}>
          <List className="w-4 h-4" /> Todas as ações
        </button>
        <button type="button" className={tabCls(view === "bloqueios")} onClick={() => setView("bloqueios")} data-testid="audit-tab-bloqueios" aria-pressed={view === "bloqueios"}>
          <ShieldAlert className={`w-4 h-4 ${view === "bloqueios" ? "" : "text-rose-600"}`} /> Bloqueios
          {resumo && <span className={`ml-1 rounded-full px-2 py-0.5 text-[11px] ${bloqueios24h ? "bg-rose-600 text-white" : "bg-slate-200 text-slate-700"}`} data-testid="audit-tab-bloqueios-count">{resumo.total}</span>}
        </button>
      </div>

      {view === "todos" && bloqueios24h > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-rose-900" data-testid="bloqueios-banner">
          <div className="flex items-center gap-2 text-sm"><ShieldAlert className="w-5 h-5" />
            <b>{bloqueios24h}</b> tentativa(s) de acesso a dados de outra empresa bloqueada(s) nas últimas 24h
            {resumo?.ultimo?.user_email ? <span className="text-rose-700"> · última: {resumo.ultimo.user_email}</span> : null}
          </div>
          <Button size="sm" variant="outline" className="border-rose-300 text-rose-800 hover:bg-rose-100" onClick={() => setView("bloqueios")} data-testid="bloqueios-banner-ver">Ver bloqueios</Button>
        </div>
      )}

      {view === "bloqueios" && <BloqueiosPanel resumo={resumo} />}

      {view === "todos" && <>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filtros</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Usuário</Label>
            <Select value={filters.user} onValueChange={(v) => setFilters({ ...filters, user: v })}>
              <SelectTrigger data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{users.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Ação</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Entidade</Label>
            <Select value={filters.entity} onValueChange={(v) => setFilters({ ...filters, entity: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Busca</Label><Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Buscar" data-testid="audit-search"/></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Logs ({filtered.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-40">Data/Hora</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="w-32">Ação</TableHead>
              {canRevert && <TableHead className="w-28 text-right">Reverter</TableHead>}
            </TableRow></TableHeader>
            <TableBody>{filtered.map((l) => (
              <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                <TableCell className="text-xs mono whitespace-nowrap align-top">{fmtDateTime(l.created_at)}</TableCell>
                <TableCell className="text-sm align-top">{frase(l)}
                  <div className="text-[10px] text-muted-foreground mt-0.5">{l.user_email}</div>
                </TableCell>
                <TableCell className="align-top"><Badge className={ACTION_COLORS[l.action] || "bg-slate-100"}>{trAction(l.action)}</Badge></TableCell>
                {canRevert && (
                  <TableCell className="align-top text-right">
                    {l.action === "bulk_assign" && l.details?.entregador_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRevertLog(l)}
                        data-testid={`revert-btn-${l.id}`}
                        title="Reverter esta atribuição em massa"
                      >
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Reverter
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={canRevert ? 4 : 3} className="text-center py-8 text-muted-foreground">Sem logs</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>}

      <RevertBulkAssignDialog
        auditLog={revertLog}
        open={!!revertLog}
        onOpenChange={(v) => !v && setRevertLog(null)}
        onDone={reload}
      />
    </div>
  );
}
